import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAgentStore, useAuthStore } from '@/store';

// Mirrors the web trigger in react-mentions: an "@" at the start of the input
// or straight after whitespace. Unlike web this query stops at a space, because
// a plain TextInput has no highlighter overlay to show where the mention ends,
// and an open-ended query would keep the list up behind a whole sentence.
const MENTION_TRIGGER = /(?:^|\s)@([^\s@]*)$/;
const SEARCH_DEBOUNCE_MS = 300;
const MAX_SUGGESTIONS = 6;

const IDLE_SEARCH = { query: null, results: null, isSearching: false };

const toSuggestion = (agent) => ({
  id: agent.id,
  name: agent.name,
  isPlusOnly: agent?.flags?.isPlusOnly,
});

const dedupeById = (lists) => {
  const seen = new Set();
  const output = [];
  for (const agent of lists.flat()) {
    if (seen.has(agent.id)) continue;
    seen.add(agent.id);
    output.push(agent);
  }
  return output;
};

/**
 * Agent @ mentions for the native composer.
 *
 * The web composer keeps `@[Name](agent://id)` markup in the input value and
 * hides it behind a highlighter overlay. A plain TextInput can't hide anything,
 * so here picking a suggestion just removes the typed "@query" — the selected
 * agent is tagged through the store and shown as a removable pill above the
 * input. `getFormattedMessage` sends `agentIds` separately from the text, so
 * targeting does not depend on any markup.
 */
export default function useAgentMentions({
  value,
  onChangeText,
  selectedAgents,
  onSetSelectedAgents,
  enabled = true,
}) {
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [search, setSearch] = useState(IDLE_SEARCH);

  const agents = useAgentStore(state => state.agents);
  const featuredAgentIds = useAgentStore(state => state.featuredAgentIds);
  const myAgentIds = useAgentStore(state => state.myAgentIds);
  const isLoadingFeaturedAgents = useAgentStore(state => state.isLoadingFeaturedAgents);
  const isFetchedFeaturedAgents = useAgentStore(state => state.isFetchedFeaturedAgents);
  const fetchFeaturedAgents = useAgentStore(state => state.fetchFeaturedAgents);
  const fetchMyAgents = useAgentStore(state => state.fetchMyAgents);
  const isSignedIn = useAuthStore(state => Boolean(state.user));

  const requestTokenRef = useRef(0);
  const requestedMyAgentsRef = useRef(false);

  const handleSelectionChange = useCallback((event) => {
    const next = event?.nativeEvent?.selection;
    if (next) setSelection(next);
  }, []);

  const activeMention = useMemo(() => {
    if (!enabled) return null;
    // One bot per message (backend limit): once an agent is tagged, a new
    // "@" must not open the suggestion list at all.
    if (selectedAgents?.length > 0) return null;
    if (selection.start !== selection.end) return null;
    const text = value || '';
    const caret = Math.min(selection.start, text.length);
    const before = text.slice(0, caret);
    const match = before.match(MENTION_TRIGGER);
    if (!match) return null;
    return { query: match[1], start: caret - match[1].length - 1 };
  }, [enabled, value, selection, selectedAgents]);

  const featuredSuggestions = useMemo(
    () => featuredAgentIds.map(id => agents[id]).filter(Boolean).map(toSuggestion),
    [featuredAgentIds, agents]
  );

  // searchAgents only covers the public catalogue, so an agent the user built
  // themselves would never come back from it.
  const mySuggestions = useMemo(
    () => myAgentIds.map(id => agents[id]).filter(Boolean).map(toSuggestion),
    [myAgentIds, agents]
  );

  useEffect(() => {
    if (!enabled || isLoadingFeaturedAgents || isFetchedFeaturedAgents) return;
    try {
      fetchFeaturedAgents();
    } catch (error) {
      console.error('[useAgentMentions] Failed to fetch featured agents:', error);
    }
  }, [enabled, isLoadingFeaturedAgents, isFetchedFeaturedAgents, fetchFeaturedAgents]);

  // Held back until the first "@" so every chat screen doesn't spend a request
  // on a menu that may never open.
  const hasActiveMention = Boolean(activeMention);
  useEffect(() => {
    if (!enabled || !hasActiveMention || !isSignedIn) return;
    if (requestedMyAgentsRef.current) return;
    requestedMyAgentsRef.current = true;
    try {
      fetchMyAgents();
    } catch (error) {
      console.error('[useAgentMentions] Failed to fetch my agents:', error);
    }
  }, [enabled, hasActiveMention, isSignedIn, fetchMyAgents]);

  // Keyed on the query text alone. Depending on anything derived from the store
  // would restart the search the moment its results landed, since searchAgents
  // writes the agents it found back into the same store.
  const query = activeMention?.query?.trim() || '';

  useEffect(() => {
    if (!query) {
      requestTokenRef.current += 1;
      setSearch(IDLE_SEARCH);
      return undefined;
    }

    setSearch({ query, results: null, isSearching: true });

    const token = ++requestTokenRef.current;
    const timeout = setTimeout(() => {
      useAgentStore.getState().searchAgents(query)
        .then(() => {
          if (token !== requestTokenRef.current) return;
          const state = useAgentStore.getState();
          const results = state.searchResultIds
            .map(id => state.agents[id])
            .filter(Boolean)
            .map(toSuggestion);
          setSearch({ query, results, isSearching: false });
        })
        .catch(() => {
          if (token !== requestTokenRef.current) return;
          setSearch({ query, results: null, isSearching: false });
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [query]);

  // The user's own agents come first and are matched locally, so they show up
  // instantly and regardless of what the catalogue search returns.
  const suggestions = useMemo(() => {
    if (!activeMention) return [];
    if (!query) {
      return dedupeById([mySuggestions, featuredSuggestions]).slice(0, MAX_SUGGESTIONS);
    }

    const lowered = query.toLowerCase();
    const matchesQuery = (agent) => agent.name?.toLowerCase().includes(lowered);
    const localMatches = [
      mySuggestions.filter(matchesQuery),
      featuredSuggestions.filter(matchesQuery),
    ];
    const serverMatches = (search.query === query && search.results) ? search.results : [];

    return dedupeById([...localMatches, serverMatches]).slice(0, MAX_SUGGESTIONS);
  }, [activeMention, query, mySuggestions, featuredSuggestions, search]);

  const applyMention = useCallback((agent) => {
    if (!activeMention) return;
    const text = value || '';
    const before = text.slice(0, activeMention.start);
    const after = text.slice(selection.start);

    // The typed "@query" is removed outright — the AgentTagChip pill owns
    // the tag; leaving "@Name" in the prompt would be redundant content.
    onChangeText?.(`${before}${after}`);

    // Move the caret ourselves rather than waiting for onSelectionChange, so a
    // stale caret can't briefly re-open the list against the new text.
    const caret = before.length;
    setSelection({ start: caret, end: caret });

    const ids = (selectedAgents || []).map(a => a.id);
    if (!ids.includes(agent.id)) {
      onSetSelectedAgents?.([...ids, agent.id]);
    }
  }, [activeMention, value, selection.start, onChangeText, selectedAgents, onSetSelectedAgents]);

  const isSearching = Boolean(activeMention) && search.isSearching && search.query === query;

  // Closes on its own: typing a space, deleting the "@", or picking an agent
  // all stop the trigger from matching.
  const isOpen = Boolean(activeMention && (suggestions.length > 0 || isSearching));

  return {
    isOpen,
    isSearching,
    suggestions,
    onSelectionChange: handleSelectionChange,
    applyMention,
  };
}
